import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";
import { CreateData } from "../../../services/process/process_data/process_data";
import { headers, baseUrl } from "../../../services/common/credentials";
import {
  Building, Users, Globe, Paperclip, UserPlus, Check, Save, Upload,
  Link, Plus, Minus, AlertCircle, CheckCircle, ChevronDown, ChevronUp,
  BookOpen, Image, FileText, Video, Eye, GraduationCap,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

const FONT = "'Inter', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#0f172a",
  muted:       "#64748b",
  light:       "#e8eef5",
  success:     "#16a34a",
  danger:      "#dc2626",
  warn:        "#d97706",
};

// ── Constants ──────────────────────────────────────────────────────────────────
const STATIC_PATH   = "/home/demo/UserManagementModule/static/reference_files/";
const UPLOAD_URL    = `${baseUrl}/api/v2/user_modules/upload/`;
const DOWNLOAD_BASE = `${baseUrl}/api/v2/user_modules/serve-pdf/`;
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

const INSTITUTION_TYPES = [
  "Affiliated College",
  "Autonomous College",
  "Deemed-to-be University College",
  "Constituent College",
  "Other",
];

const NAAC_GRADES = ["A++", "A+", "A", "B++", "B+", "B", "C", "D", "Not Accredited"];

const APPROVAL_STATUSES = [
  "AICTE Approved",
  "UGC Approved",
  "AICTE & UGC Approved",
  "Awaiting Approval",
  "Not Applicable",
];

const COLLABORATION_AREAS = [
  "Student Exchange",
  "Faculty Exchange",
  "Joint Research",
  "International Internships",
  "Dual Degree Programs",
  "Study Abroad Sessions",
  "Industry Collaboration",
  "Other",
];

// Upload configs
const LOGO_CONFIG     = { key: "logo",    label: "Institution Logo",        hint: "PNG or JPG with transparent background preferred · Max 5 MB",  accept: ".png,.jpg,.jpeg", maxMB: 5 };
const POSTER_CONFIG   = { key: "poster",  label: "Event Poster",            hint: "PNG/JPG · Max 10 MB",                                           accept: ".png,.jpg,.jpeg", maxMB: 10 };
const IMAGES_CONFIG   = { key: "images",  label: "Additional Event Images", hint: "PNG/JPG · Up to 10 images · Max 10 MB each",                    accept: ".png,.jpg,.jpeg", maxMB: 10, multiple: true };

// ── Shared UI Atoms ────────────────────────────────────────────────────────────
const TopNav = ({ userCredentials }) => (
  <div style={{
    background: `linear-gradient(135deg, ${T.navyDark} 0%, ${T.navy} 60%, ${T.navyMid} 100%)`,
    padding: "0 32px", height: 56,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    boxShadow: "0 2px 12px rgba(6,21,53,.45)",
    position: "sticky", top: 0, zIndex: 100,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: T.orange,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <GraduationCap size={18} color="#fff" />
      </div>
      <span style={{ color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: .2 }}>
        College Registration Portal
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 34, height: 34, borderRadius: "50%", background: T.orange,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 800, color: "#fff", fontSize: 14,
      }}>
        {(userCredentials?.display_name || userCredentials?.user_name || "U")[0].toUpperCase()}
      </div>
      <span style={{ color: "rgba(255,255,255,.85)", fontSize: 13, fontWeight: 600 }}>
        {userCredentials?.display_name || userCredentials?.user_name || "User"}
      </span>
    </div>
  </div>
);

const Card = ({ children, style = {} }) => (
  <div style={{
    background: T.card, borderRadius: 14,
    border: `1px solid ${T.border}`,
    boxShadow: "0 4px 20px rgba(10,35,82,.07)",
    overflow: "hidden", ...style,
  }}>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, badge, isOpen, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 20px",
      background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`,
      cursor: "pointer", userSelect: "none",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        background: T.orange, borderRadius: 7, padding: "5px 6px",
        display: "flex", alignItems: "center",
      }}>
        <Icon size={15} color="#fff" />
      </div>
      <span style={{ color: "#fff", fontWeight: 800, fontSize: 14, letterSpacing: .2 }}>{title}</span>
      {badge != null && (
        <span style={{
          background: T.orange, color: "#fff",
          borderRadius: 99, padding: "1px 8px", fontSize: 11, fontWeight: 800,
        }}>{badge}</span>
      )}
    </div>
    {isOpen
      ? <ChevronUp size={18} color="rgba(255,255,255,.75)" />
      : <ChevronDown size={18} color="rgba(255,255,255,.75)" />}
  </div>
);

const SubHeader = ({ title }) => (
  <div style={{
    fontSize: 11, fontWeight: 800, color: T.navy,
    textTransform: "uppercase", letterSpacing: .6,
    marginBottom: 12, marginTop: 4,
    paddingBottom: 6, borderBottom: `2px solid ${T.light}`,
    fontFamily: FONT,
  }}>
    {title}
  </div>
);

const Field = ({ label, required, children, hint, fullWidth, style: fieldStyle = {} }) => (
  <div style={{ marginBottom: 14, gridColumn: fullWidth ? "1/-1" : undefined, ...fieldStyle }}>
    <label style={{
      display: "block", fontSize: 12, fontWeight: 700, color: T.navy,
      marginBottom: 5, letterSpacing: .3, textTransform: "uppercase",
    }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 11, color: T.muted, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

const inputBase = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 13, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border .18s, box-shadow .18s", fontFamily: FONT,
};

const Input = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Select = ({ children, style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

const Textarea = ({ style = {}, maxLength, value, ...props }) => {
  const [focused, setFocused] = useState(false);
  const wordCount = value ? value.trim().split(/\s+/).filter(Boolean).length : 0;
  return (
    <div>
      <textarea
        value={value}
        {...props}
        style={{
          ...inputBase,
          borderColor: focused ? T.orange : T.border,
          boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
          resize: "vertical", minHeight: 90, ...style,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {maxLength && (
        <p style={{
          fontSize: 11, margin: "3px 0 0", textAlign: "right",
          color: wordCount > maxLength ? T.danger : T.muted,
        }}>
          {wordCount} / {maxLength} words
        </p>
      )}
    </div>
  );
};

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const pad = size === "sm" ? "7px 14px" : "10px 22px";
  const fs = size === "sm" ? 12 : 13;
  const variants = {
    primary:   { background: hovered ? T.orangeHover : T.orange, color: "#fff", border: "none" },
    secondary: { background: hovered ? T.light : "#f1f5f9", color: T.navy, border: `1.5px solid ${T.border}` },
    navy:      { background: hovered ? T.navyMid : T.navy, color: "#fff", border: "none" },
    success:   { background: hovered ? "#15803d" : T.success, color: "#fff", border: "none" },
    outline:   {
      background: "transparent", color: T.navy, border: `1.5px solid ${T.border}`,
      ...(hovered && { borderColor: T.orange, color: T.orange }),
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        borderRadius: 8, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
        padding: pad, fontSize: fs, transition: "all .15s",
        opacity: disabled ? .6 : 1, fontFamily: FONT,
        ...variants[variant], ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 13 : 14} />}
      {children}
    </button>
  );
};

const Checkbox = ({ checked, onChange, label, required }) => (
  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 10 }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `2px solid ${checked ? T.orange : T.border}`,
        background: checked ? T.orange : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all .15s", cursor: "pointer",
      }}
    >
      {checked && <Check size={11} color="#fff" strokeWidth={3} />}
    </div>
    <span style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
      {label}
      {required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </span>
  </label>
);

// Multi-checkbox toggle for collaboration areas
const CollabToggle = ({ value, onChange }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
    {COLLABORATION_AREAS.map(area => {
      const active = value.includes(area);
      return (
        <button
          key={area}
          onClick={() => onChange(active ? value.filter(x => x !== area) : [...value, area])}
          style={{
            padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            border: `1.5px solid ${active ? T.orange : T.border}`,
            background: active ? "rgba(249,115,22,.1)" : "#fff",
            color: active ? T.orange : T.muted,
            cursor: "pointer", transition: "all .15s", fontFamily: FONT,
          }}
        >
          {area}
        </button>
      );
    })}
  </div>
);

const ToggleSwitch = ({ value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{
      width: 40, height: 22, borderRadius: 99, cursor: "pointer",
      background: value ? T.orange : T.border,
      position: "relative", transition: "background .2s", flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 3,
      left: value ? 20 : 3,
      width: 16, height: 16, borderRadius: "50%",
      background: "#fff", transition: "left .2s",
      boxShadow: "0 1px 4px rgba(0,0,0,.2)",
    }} />
  </div>
);

const DonutChart = ({ pct, size = 88 }) => {
  const r = 36, cx = 44, cy = 44;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 88 88">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.light} strokeWidth={14} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={pct > 80 ? T.success : pct > 50 ? T.orange : T.danger}
        strokeWidth={14}
        strokeDasharray={`${filled} ${circ - filled}`}
        style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dasharray .5s" }}
      />
      <text x="44" y="49" textAnchor="middle" fontSize="15" fontWeight="900" fill={T.navy}>{pct}%</text>
    </svg>
  );
};

const ProgressBar = ({ value, color = T.orange }) => (
  <div style={{ background: T.light, borderRadius: 99, height: 6, overflow: "hidden" }}>
    <div style={{
      width: `${Math.min(value, 100)}%`, height: "100%",
      background: color, borderRadius: 99, transition: "width .5s",
    }} />
  </div>
);

// ── Single File Upload Row ─────────────────────────────────────────────────────
const SingleFileRow = ({ config, uploaded, isUploading, onChoose, onClear }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 16, padding: "12px 16px",
    background: uploaded ? "rgba(249,115,22,.05)" : T.bg,
    borderRadius: 10,
    border: `1.5px dashed ${uploaded ? T.orange : T.border}`,
    fontFamily: FONT,
  }}>
    <div style={{
      background: uploaded ? "rgba(249,115,22,.15)" : T.light,
      borderRadius: 8, padding: 10, flexShrink: 0,
    }}>
      <Upload size={16} color={uploaded ? T.orange : T.navy} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{config.label}</div>
      {isUploading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
          <div style={{
            width: 11, height: 11, border: `2px solid ${T.light}`,
            borderTop: `2px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }} />
          <span style={{ fontSize: 12, color: T.orange, fontWeight: 600 }}>Uploading…</span>
        </div>
      ) : uploaded ? (
        <a href={`${DOWNLOAD_BASE}${uploaded.fileName}`} target="_blank" rel="noopener noreferrer"
          style={{
            fontSize: 12, color: "#007bff", textDecoration: "underline",
            display: "block", marginTop: 3, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
          {uploaded.fileName}
        </a>
      ) : (
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{config.hint}</div>
      )}
    </div>
    {!isUploading && (
      uploaded ? (
        <button onClick={onClear} title="Remove file" style={{
          cursor: "pointer", background: "none", border: "none",
          color: T.danger, fontWeight: 800, fontSize: 15, lineHeight: 1,
          padding: "2px 7px", flexShrink: 0,
        }}>✕</button>
      ) : (
        <button onClick={onChoose} style={{
          background: "transparent", border: `1.5px solid ${T.border}`,
          borderRadius: 7, padding: "5px 14px", color: T.navy,
          fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center",
          gap: 5, flexShrink: 0, cursor: "pointer", fontFamily: FONT,
        }}>
          <Upload size={12} /> Choose File
        </button>
      )
    )}
  </div>
);

const MultiFileList = ({ files, onRemove, onAdd, isUploading, config }) => (
  <div>
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
      {files.map((f, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 12px", background: "rgba(249,115,22,.05)",
          borderRadius: 8, border: `1px solid rgba(249,115,22,.2)`,
        }}>
          <FileText size={14} color={T.orange} />
          <a href={`${DOWNLOAD_BASE}${f.fileName}`} target="_blank" rel="noopener noreferrer"
            style={{
              flex: 1, fontSize: 12, color: "#007bff", textDecoration: "underline",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
            {f.fileName}
          </a>
          <button onClick={() => onRemove(i)} style={{
            background: "none", border: "none", color: T.danger,
            cursor: "pointer", padding: "0 4px", fontWeight: 800, fontSize: 13,
          }}>✕</button>
        </div>
      ))}
    </div>
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
      background: T.bg, borderRadius: 10,
      border: `1.5px dashed ${T.border}`, fontFamily: FONT,
    }}>
      <div style={{ background: T.light, borderRadius: 8, padding: 8, flexShrink: 0 }}>
        <Upload size={15} color={T.navy} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{config.label}</div>
        {isUploading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
            <div style={{
              width: 10, height: 10, border: `2px solid ${T.light}`,
              borderTop: `2px solid ${T.orange}`, borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
            }} />
            <span style={{ fontSize: 11, color: T.orange, fontWeight: 600 }}>Uploading…</span>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{config.hint}</div>
        )}
      </div>
      {!isUploading && (
        <button onClick={onAdd} style={{
          background: "transparent", border: `1.5px solid ${T.border}`,
          borderRadius: 7, padding: "5px 14px", color: T.navy,
          fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center",
          gap: 5, cursor: "pointer", fontFamily: FONT,
        }}>
          <Plus size={12} /> Add File
        </button>
      )}
    </div>
  </div>
);

// ── Step indicator ─────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Institution Info",  icon: Building      },
  { label: "Coordinator",       icon: Users         },
  { label: "Media Uploads",     icon: Image         },
  { label: "Collaboration",     icon: Globe         },
  { label: "Declaration",       icon: Check         },
];

const StepBar = ({ current }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "18px 32px 0", gap: 0, fontFamily: FONT,
  }}>
    {STEPS.map((s, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: done ? T.success : active ? T.orange : T.light,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `2px solid ${done ? T.success : active ? T.orange : T.border}`,
              transition: "all .2s",
            }}>
              {done
                ? <Check size={15} color="#fff" strokeWidth={3} />
                : <s.icon size={14} color={active ? "#fff" : T.muted} />}
            </div>
            <span style={{
              fontSize: 10, fontWeight: 700, color: active ? T.orange : done ? T.success : T.muted,
              marginTop: 4, textAlign: "center", letterSpacing: .3, textTransform: "uppercase",
            }}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, background: done ? T.success : T.light,
              margin: "-18px 4px 0", transition: "background .3s",
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
function CollegeRegistration() {
  const uc = getUserCredentials();
  const [step, setStep]                       = useState(0);
  const [existingProfile, setExistingProfile] = useState(null);
  const [saving, setSaving]                   = useState(false);
  const [saveStatus, setSaveStatus]           = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [previewMode, setPreviewMode]         = useState(false);

  // ── Form data ──────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    // Institution Information
    college_name: "",
    institution_type: "",
    other_institution_type: "",
    affiliated_university: "",
    year_established: "",
    naac_grade: "",
    nirf_ranking: "",
    approval_status: "",
    website_url: "",
    official_email: "",
    phone_number: "",
    // Address
    address_line1: "", address_line2: "",
    city: "", state: "", pin_code: "", country: "India",
    // Primary Coordinator
    coordinator_name: "",
    coordinator_designation: "",
    coordinator_department: "",
    coordinator_mobile: "",
    coordinator_email: "",

    // Media
    institution_logo: "",
    promo_video_url: "",
    // Social
    instagram_handle: "",
    linkedin_page: "",
    facebook_page: "",
    twitter_handle: "",
    // Collaboration
    intl_university_collab: false,
    collaboration_areas: [],
    host_intl_delegates: false,
    // Declaration
    consent_accuracy: false,
    consent_media: false,
    authorized_signatory_name: "",
    authorized_signatory_designation: "",
    declaration_date: "",
  });

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 0:
        return (
          formData.college_name &&
          formData.institution_type &&
          formData.official_email &&
          formData.phone_number &&
          formData.address_line1 &&
          formData.city &&
          formData.state &&
          formData.pin_code
        );
      case 1:
        return (
          formData.coordinator_name &&
          formData.coordinator_designation &&
          formData.coordinator_department &&
          formData.coordinator_mobile &&
          formData.coordinator_email
        );
      case 2:
        return logoFile !== null;
      case 3:
        return true; // collaboration optional
      case 4:
        return (
          formData.consent_accuracy &&
          formData.consent_media &&
          formData.authorized_signatory_name &&
          formData.authorized_signatory_designation &&
          formData.declaration_date
        );
      default:
        return true;
    }
  };

  // ── Alternate Representatives ──────────────────────────────────────────────
  const [alternateReps, setAlternateReps] = useState([{ name: "", email: "", phone: "" }]);
  const addAltRep    = () => setAlternateReps(p => [...p, { name: "", email: "", phone: "" }]);
  const removeAltRep = i  => setAlternateReps(p => p.filter((_, idx) => idx !== i));
  const updateAltRep = (i, key, value) => {
    const r = [...alternateReps];
    r[i][key] = value;
    setAlternateReps(r);
  };

  // ── File uploads ───────────────────────────────────────────────────────────
  const [logoFile, setLogoFile]         = useState(null);

  const [uploadingKeys, setUploadingKeys] = useState({});

  const logoRef    = useRef(null);
  const posterRef  = useRef(null);
  const imagesRef  = useRef(null);

  // ── Collapsible sections ───────────────────────────────────────────────────
  const [openSections, setOpenSections] = useState({
    basic: true, address: true, accreditation: true,
    coordinator: true, alternate: true,
    logo: true, poster: true, images: true, video: true, social: true,
    collab: true, delegates: true,
    declaration: true,
  });
  const toggleSection = k => setOpenSections(p => ({ ...p, [k]: !p[k] }));

  // ── Load existing profile ──────────────────────────────────────────────────
  useEffect(() => { fetchExistingProfile(); }, []);

  const fetchExistingProfile = async () => {
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT * FROM edu_platform.college_profile WHERE user_id = ${uc.user_id} AND is_active = 1 LIMIT 1;`,
      }, creds[0]);
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        setExistingProfile(row.primary_id);
        setFormData(prev => ({
          ...prev,
          college_name:                    row.college_name                    || "",
          institution_type:                row.institution_type                || "",
          other_institution_type:          row.other_institution_type          || "",
          affiliated_university:           row.affiliated_university           || "",
          year_established:                row.year_established                || "",
          naac_grade:                      row.naac_grade                      || "",
          nirf_ranking:                    row.nirf_ranking                    || "",
          approval_status:                 row.approval_status                 || "",
          website_url:                     row.website_url                     || "",
          official_email:                  row.official_email                  || "",
          phone_number:                    row.phone_number                    || "",
          address_line1:                   row.address_line1                   || "",
          address_line2:                   row.address_line2                   || "",
          city:                            row.city                            || "",
          state:                           row.state                           || "",
          pin_code:                        row.pin_code                        || "",
          country:                         row.country                         || "India",
          coordinator_name:                row.coordinator_name                || "",
          coordinator_designation:         row.coordinator_designation         || "",
          coordinator_department:          row.coordinator_department          || "",
          coordinator_mobile:              row.coordinator_mobile              || "",
          coordinator_email:               row.coordinator_email               || "",
          institution_logo:                row.institution_logo                || "",
          promo_video_url:                 row.promo_video_url                 || "",
          instagram_handle:                row.instagram_handle                || "",
          linkedin_page:                   row.linkedin_page                   || "",
          facebook_page:                   row.facebook_page                   || "",
          twitter_handle:                  row.twitter_handle                  || "",
          intl_university_collab:          !!row.intl_university_collab,
          collaboration_areas:             row.collaboration_areas ? JSON.parse(row.collaboration_areas) : [],
          host_intl_delegates:             !!row.host_intl_delegates,
          consent_accuracy:                !!row.consent_accuracy,
          consent_media:                   !!row.consent_media,
          authorized_signatory_name:       row.authorized_signatory_name       || "",
          authorized_signatory_designation:row.authorized_signatory_designation|| "",
          declaration_date:                row.declaration_date                || "",
        }));
        if (row.institution_logo) setLogoFile({ fileName: row.institution_logo });

        if (row.alternate_reps) {
          try { setAlternateReps(JSON.parse(row.alternate_reps)); }
          catch { setAlternateReps([{ name: "", email: "", phone: "" }]); }
        }
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) =>
    setFormData(p => ({ ...p, [name]: value }));

  // ── File upload ────────────────────────────────────────────────────────────
  const uploadFile = async (key, maxMB, file) => {
    if (!file) return null;
    if (file.size > maxMB * 1024 * 1024) {
      alert(`File too large. Maximum size is ${maxMB} MB.`);
      return null;
    }
    setUploadingKeys(p => ({ ...p, [key]: true }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filePath", STATIC_PATH);
    try {
      const res = await fetch(UPLOAD_URL, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        return { fileName: data.Filename, filePath: data.Filepath };
      } else {
        const err = await res.json();
        alert("Upload failed: " + (err.Message || "Unknown error"));
        return null;
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed. Please try again.");
      return null;
    } finally {
      setUploadingKeys(p => ({ ...p, [key]: false }));
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const result = await uploadFile("logo", LOGO_CONFIG.maxMB, file);
    if (result) { setLogoFile(result); handleChange("institution_logo", result.fileName); }
    if (logoRef.current) logoRef.current.value = "";
  };


  // ── Completion ─────────────────────────────────────────────────────────────
  const requiredFields = [
    "college_name", "institution_type", "official_email", "phone_number",
    "address_line1", "city", "state", "pin_code",
    "coordinator_name", "coordinator_designation", "coordinator_department",
    "coordinator_mobile", "coordinator_email",
  ];
  const filled = requiredFields.filter(f => {
    const v = formData[f];
    return v && String(v).trim() !== "";
  }).length;
  const completion = Math.round((filled / requiredFields.length) * 100);
  const consentDone = formData.consent_accuracy && formData.consent_media &&
    formData.authorized_signatory_name && formData.authorized_signatory_designation && formData.declaration_date;
  const missingRequired = requiredFields.filter(f => !formData[f] || String(formData[f]).trim() === "");

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (status) => {
    setSaving(true);
    setSaveStatus(null);
    const now = getFormattedDate();
    const payload_data = {
      ...formData,
      collaboration_areas: JSON.stringify(formData.collaboration_areas),
      alternate_reps:      JSON.stringify(alternateReps),
      profile_status:      status,
      modified_date:       now,
      modified_by:         uc.user_id,
      user_id:             uc.user_id,
      is_active:           1,
    };
    try {
      if (existingProfile) {
        const creds = Fetchupdatequery();
        await axios.post(creds[1], {
          schema_name: "edu_platform",
          table: "college_profile",
          set_column: [payload_data],
          where: [{ primary_id: existingProfile }],
        }, creds[0]);
      } else {
        const creds = CreateData();
        await axios.post(creds[1], {
          database: "edu_platform",
          table: "college_profile",
          formData: {
            ...payload_data,
            tenant_id:    uc.tenants_id,
            entity_id:    uc.selectedentity || uc.entities_id,
            group_id:     uc.groups_id,
            created_by:   uc.user_id,
            created_date: now,
          },
        }, creds[0]);
        await fetchExistingProfile();
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 3500);
    } catch (err) {
      console.error("Save error:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: T.navy }}>
          <div style={{
            width: 44, height: 44, border: `4px solid ${T.light}`,
            borderTop: `4px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.8s linear infinite", margin: "0 auto 12px",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontWeight: 700, fontSize: 14, margin: 0, fontFamily: FONT }}>Loading…</p>
        </div>
      </div>
    );
  }

  // ── Step renderers ─────────────────────────────────────────────────────────

  // STEP 0 — Institution Information
  const renderStep0 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Basic Details */}
      <Card>
        <SectionHeader icon={Building} title="Basic Details" isOpen={openSections.basic} onToggle={() => toggleSection("basic")} />
        {openSections.basic && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(250px,1fr))", gap: 16 }}>
            <Field label="Name of the College / Institution" required fullWidth>
              <Input value={formData.college_name} onChange={e => handleChange("college_name", e.target.value)} placeholder="Enter full institution name" />
            </Field>
            <Field label="Type of Institution" required>
              <Select value={formData.institution_type} onChange={e => handleChange("institution_type", e.target.value)}>
                <option value="">Select type</option>
                {INSTITUTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            {formData.institution_type === "Other" && (
              <Field label="Specify Institution Type" required>
                <Input value={formData.other_institution_type} onChange={e => handleChange("other_institution_type", e.target.value)} placeholder="Describe institution type" />
              </Field>
            )}
            <Field label="Name of Affiliated University" hint="Leave blank if not applicable">
              <Input value={formData.affiliated_university} onChange={e => handleChange("affiliated_university", e.target.value)} placeholder="e.g. University of Mumbai" />
            </Field>
            <Field label="Year of Establishment">
              <Input type="number" value={formData.year_established} onChange={e => handleChange("year_established", e.target.value)} placeholder="e.g. 1985" min="1800" max={new Date().getFullYear()} />
            </Field>
            <Field label="Institution Website">
              <Input value={formData.website_url} onChange={e => handleChange("website_url", e.target.value)} placeholder="https://yourcollege.edu.in" />
            </Field>
            <Field label="Official Email Address" required>
              <Input type="email" value={formData.official_email} onChange={e => handleChange("official_email", e.target.value)} placeholder="info@college.edu.in" />
            </Field>
            <Field label="Official Contact Number" required>
              <Input value={formData.phone_number} onChange={e => handleChange("phone_number", e.target.value)} placeholder="+91-XXXXXXXXXX" />
            </Field>
          </div>
        )}
      </Card>

      {/* Accreditation & Rankings */}
      <Card>
        <SectionHeader icon={BookOpen} title="Accreditation & Rankings" isOpen={openSections.accreditation} onToggle={() => toggleSection("accreditation")} />
        {openSections.accreditation && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(250px,1fr))", gap: 16 }}>
            <Field label="NAAC Accreditation Grade">
              <Select value={formData.naac_grade} onChange={e => handleChange("naac_grade", e.target.value)}>
                <option value="">Select grade</option>
                {NAAC_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </Select>
            </Field>
            <Field label="NIRF Ranking" hint="Leave blank if not ranked">
              <Input type="number" value={formData.nirf_ranking} onChange={e => handleChange("nirf_ranking", e.target.value)} placeholder="e.g. 125" min="1" />
            </Field>
            <Field label="AICTE / UGC Approval Status">
              <Select value={formData.approval_status} onChange={e => handleChange("approval_status", e.target.value)}>
                <option value="">Select status</option>
                {APPROVAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
          </div>
        )}
      </Card>

      {/* Address */}
      <Card>
        <SectionHeader icon={Building} title="Institutional Address" isOpen={openSections.address} onToggle={() => toggleSection("address")} />
        {openSections.address && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(250px,1fr))", gap: 16 }}>
            <Field label="Address Line 1" required fullWidth>
              <Input value={formData.address_line1} onChange={e => handleChange("address_line1", e.target.value)} placeholder="Building / Street" />
            </Field>
            <Field label="Address Line 2" fullWidth>
              <Input value={formData.address_line2} onChange={e => handleChange("address_line2", e.target.value)} placeholder="Area / Landmark (optional)" />
            </Field>
            <Field label="City" required>
              <Input value={formData.city} onChange={e => handleChange("city", e.target.value)} placeholder="City" />
            </Field>
            <Field label="State" required>
              <Select value={formData.state} onChange={e => handleChange("state", e.target.value)}>
                <option value="">Select state / UT</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
            <Field label="PIN Code" required>
              <Input value={formData.pin_code} onChange={e => handleChange("pin_code", e.target.value)} placeholder="6-digit PIN code" maxLength={6} />
            </Field>
            <Field label="Country">
              <Input value={formData.country} readOnly style={{ background: T.light, cursor: "not-allowed" }} />
            </Field>
          </div>
        )}
      </Card>
    </div>
  );

  // STEP 1 — Coordinator Details
  const renderStep1 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Primary Coordinator */}
      <Card>
        <SectionHeader icon={Users} title="Primary Coordinator Details" isOpen={openSections.coordinator} onToggle={() => toggleSection("coordinator")} />
        {openSections.coordinator && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(250px,1fr))", gap: 16 }}>
            <Field label="Full Name" required>
              <Input value={formData.coordinator_name} onChange={e => handleChange("coordinator_name", e.target.value)} placeholder="Full name" />
            </Field>
            <Field label="Designation" required>
              <Input value={formData.coordinator_designation} onChange={e => handleChange("coordinator_designation", e.target.value)} placeholder="e.g. Head of Department" />
            </Field>
            <Field label="Department" required>
              <Input value={formData.coordinator_department} onChange={e => handleChange("coordinator_department", e.target.value)} placeholder="e.g. Computer Science" />
            </Field>
            <Field label="Mobile Number" required>
              <Input value={formData.coordinator_mobile} onChange={e => handleChange("coordinator_mobile", e.target.value)} placeholder="+91-XXXXXXXXXX" />
            </Field>
            <Field label="Official Email ID" required>
              <Input type="email" value={formData.coordinator_email} onChange={e => handleChange("coordinator_email", e.target.value)} placeholder="coordinator@college.edu.in" />
            </Field>
          </div>
        )}
      </Card>

      {/* Alternate / Additional Representatives */}
      <Card>
        <SectionHeader
          icon={UserPlus}
          title="Additional Representatives"
          badge={alternateReps.length}
          isOpen={openSections.alternate}
          onToggle={() => toggleSection("alternate")}
        />
        {openSections.alternate && (
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: T.navy, textTransform: "uppercase", letterSpacing: .6, paddingBottom: 6, borderBottom: `2px solid ${T.light}`, flex: 1, marginRight: 16, fontFamily: FONT }}>
                Representatives ({alternateReps.length})
              </div>
              <Btn variant="outline" size="sm" icon={UserPlus} onClick={addAltRep}>Add Rep</Btn>
            </div>

            {/* Column headers */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr 1fr 40px",
              gap: 10, marginBottom: 8, padding: "0 13px", boxSizing: "border-box",
            }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>Name</div>
              <div style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>Email</div>
              <div style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>Mobile</div>
              <div />
            </div>

            {alternateReps.map((rep, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr 40px",
                gap: 10, marginBottom: 10, padding: 12,
                background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`,
                boxSizing: "border-box",
              }}>
                <Input
                  placeholder="Full name"
                  value={rep.name}
                  onChange={e => updateAltRep(i, "name", e.target.value)}
                />
                <Input
                  placeholder="Email address"
                  type="email"
                  value={rep.email}
                  onChange={e => updateAltRep(i, "email", e.target.value)}
                />
                <Input
                  placeholder="+91-XXXXXXXXXX"
                  value={rep.phone}
                  onChange={e => updateAltRep(i, "phone", e.target.value)}
                />
                <button
                  onClick={() => removeAltRep(i)}
                  disabled={alternateReps.length === 1}
                  style={{
                    width: 40, height: "100%",
                    background: alternateReps.length === 1 ? T.light : "rgba(220,38,38,.1)",
                    border: "none", borderRadius: 8,
                    cursor: alternateReps.length === 1 ? "not-allowed" : "pointer",
                    color: T.danger, display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <Minus size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );

  // STEP 2 — Media & Branding
  const renderStep2 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Logo */}
      <Card>
        <SectionHeader icon={Image} title="Institution Logo" isOpen={openSections.logo} onToggle={() => toggleSection("logo")} />
        {openSections.logo && (
          <div style={{ padding: 20 }}>
            <input ref={logoRef} type="file" accept={LOGO_CONFIG.accept} style={{ display: "none" }} onChange={handleLogoChange} />
            <SingleFileRow
              config={LOGO_CONFIG}
              uploaded={logoFile}
              isUploading={!!uploadingKeys.logo}
              onChoose={() => logoRef.current?.click()}
              onClear={() => { setLogoFile(null); handleChange("institution_logo", ""); }}
            />
          </div>
        )}
      </Card>


      {/* Video Link */}
      <Card>
        <SectionHeader icon={Video} title="Promotional Video" isOpen={openSections.video} onToggle={() => toggleSection("video")} />
        {openSections.video && (
          <div style={{ padding: 20 }}>
            <Field label="YouTube / Vimeo URL" hint="Link to a promotional or event highlight video">
              <div style={{ display: "flex" }}>
                <div style={{
                  background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                  borderRadius: "8px 0 0 8px", padding: "9px 12px",
                  display: "flex", alignItems: "center",
                }}>
                  <Video size={14} color={T.muted} />
                </div>
                <Input
                  value={formData.promo_video_url}
                  onChange={e => handleChange("promo_video_url", e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  style={{ borderRadius: "0 8px 8px 0" }}
                />
              </div>
            </Field>
          </div>
        )}
      </Card>

      {/* Social Media */}
      <Card>
        <SectionHeader icon={Globe} title="Social Media Handles" isOpen={openSections.social} onToggle={() => toggleSection("social")} />
        {openSections.social && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { key: "instagram_handle", label: "Instagram",    icon: Globe, placeholder: "@yourcollege" },
              { key: "linkedin_page",    label: "LinkedIn",     icon: Link,  placeholder: "https://linkedin.com/school/..." },
              { key: "facebook_page",    label: "Facebook",     icon: Link,  placeholder: "https://facebook.com/yourcollege" },
              { key: "twitter_handle",   label: "X (Twitter)",  icon: Globe, placeholder: "@yourcollege" },
            ].map(s => (
              <Field key={s.key} label={s.label}>
                <div style={{ display: "flex" }}>
                  <div style={{
                    background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                    borderRadius: "8px 0 0 8px", padding: "9px 12px",
                    display: "flex", alignItems: "center",
                  }}>
                    <s.icon size={14} color={T.muted} />
                  </div>
                  <Input
                    value={formData[s.key]}
                    onChange={e => handleChange(s.key, e.target.value)}
                    placeholder={s.placeholder}
                    style={{ borderRadius: "0 8px 8px 0" }}
                  />
                </div>
              </Field>
            ))}
          </div>
        )}
      </Card>
    </div>
  );

  // STEP 3 — Collaboration & Partnership
  const renderStep3 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Card>
        <SectionHeader icon={Globe} title="Collaboration & Partnership Interest" isOpen={openSections.collab} onToggle={() => toggleSection("collab")} />
        {openSections.collab && (
          <div style={{ padding: 24 }}>
            {/* International University Collaboration toggle */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 18px", marginBottom: 20,
              background: formData.intl_university_collab ? "rgba(249,115,22,.05)" : T.bg,
              borderRadius: 10,
              border: `1.5px solid ${formData.intl_university_collab ? "rgba(249,115,22,.25)" : T.border}`,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Interested in International University Collaboration?</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Formal partnerships, exchange programmes, and academic collaborations with overseas universities</div>
              </div>
              <ToggleSwitch value={formData.intl_university_collab} onChange={v => handleChange("intl_university_collab", v)} />
            </div>

            {/* Areas of Interest (shown when toggle is ON) */}
            {formData.intl_university_collab && (
              <div style={{ marginBottom: 20 }}>
                <Field label="Areas of Interest" hint="Select all that apply">
                  <CollabToggle
                    value={formData.collaboration_areas}
                    onChange={v => handleChange("collaboration_areas", v)}
                  />
                </Field>
              </div>
            )}

            {/* Host International Delegates toggle */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 18px",
              background: formData.host_intl_delegates ? "rgba(249,115,22,.05)" : T.bg,
              borderRadius: 10,
              border: `1.5px solid ${formData.host_intl_delegates ? "rgba(249,115,22,.25)" : T.border}`,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Interested in Hosting International Delegates?</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Welcome visiting scholars, delegations, or partner institution representatives on campus</div>
              </div>
              <ToggleSwitch value={formData.host_intl_delegates} onChange={v => handleChange("host_intl_delegates", v)} />
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  // STEP 4 — Declaration
  const renderStep4 = () => (
    <Card>
      <SectionHeader icon={Check} title="Declaration" isOpen={openSections.declaration} onToggle={() => toggleSection("declaration")} />
      {openSections.declaration && (
        <div style={{ padding: 24 }}>
          <div style={{
            background: "rgba(249,115,22,.06)", borderRadius: 10,
            border: `1px solid rgba(249,115,22,.2)`, padding: "16px 20px",
            marginBottom: 24,
          }}>
            <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.7, fontFamily: FONT }}>
              Please read each declaration carefully before confirming your submission. Both consents and the authorized signatory details are required to complete registration.
            </p>
          </div>

          <Checkbox
            checked={formData.consent_accuracy}
            onChange={v => handleChange("consent_accuracy", v)}
            required
            label="We hereby confirm that all information submitted is accurate and officially authorized by our institution."
          />
          <Checkbox
            checked={formData.consent_media}
            onChange={v => handleChange("consent_media", v)}
            required
            label="We consent to the use of submitted logos, event details, and photographs for promotional and collaboration purposes."
          />

          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(3, minmax(200px,1fr))", gap: 16 }}>
            <Field label="Name of Authorized Signatory" required>
              <Input
                value={formData.authorized_signatory_name}
                onChange={e => handleChange("authorized_signatory_name", e.target.value)}
                placeholder="Full name"
              />
            </Field>
            <Field label="Designation" required>
              <Input
                value={formData.authorized_signatory_designation}
                onChange={e => handleChange("authorized_signatory_designation", e.target.value)}
                placeholder="e.g. Principal / Director"
              />
            </Field>
            <Field label="Date" required>
              <Input
                type="date"
                value={formData.declaration_date}
                onChange={e => handleChange("declaration_date", e.target.value)}
              />
            </Field>
          </div>

          {!consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(220,38,38,.07)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <AlertCircle size={15} color={T.danger} />
              <span style={{ fontSize: 12, color: T.danger, fontWeight: 600, fontFamily: FONT }}>
                Both declarations and all signatory details must be provided to submit registration.
              </span>
            </div>
          )}
          {consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(22,163,74,.1)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <CheckCircle size={15} color={T.success} />
              <span style={{ fontSize: 12, color: T.success, fontWeight: 700, fontFamily: FONT }}>
                All declarations accepted. Ready to submit.
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  // ── Preview Modal ──────────────────────────────────────────────────────────
  const PreviewModal = () => (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(6,21,53,.55)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      <div style={{
        background: T.card, borderRadius: 16, maxWidth: 640,
        width: "100%", maxHeight: "85vh", overflow: "auto",
        boxShadow: "0 24px 60px rgba(6,21,53,.3)",
        fontFamily: FONT,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`,
          padding: "18px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0,
        }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>Submission Preview</span>
          <button onClick={() => setPreviewMode(false)} style={{
            background: "rgba(255,255,255,.15)", border: "none", borderRadius: 6,
            padding: "4px 12px", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 12,
          }}>Close</button>
        </div>
        <div style={{ padding: 24 }}>
          {[
            ["Institution Name", formData.college_name],
            ["Institution Type", formData.institution_type === "Other" ? formData.other_institution_type : formData.institution_type],
            ["Affiliated University", formData.affiliated_university || "—"],
            ["Year Established", formData.year_established || "—"],
            ["NAAC Grade", formData.naac_grade || "—"],
            ["NIRF Ranking", formData.nirf_ranking || "—"],
            ["Approval Status", formData.approval_status || "—"],
            ["Address", [formData.address_line1, formData.address_line2, formData.city, formData.state, formData.pin_code].filter(Boolean).join(", ")],
            ["Official Email", formData.official_email],
            ["Phone", formData.phone_number],
            ["Website", formData.website_url || "—"],
            ["Primary Coordinator", formData.coordinator_name],
            ["Coordinator Email", formData.coordinator_email],
            ["Institution Logo", formData.institution_logo || "Not uploaded"],
            ["Promo Video", formData.promo_video_url || "—"],
            ["Intl. Collaboration", formData.intl_university_collab ? "Yes" : "No"],
            ["Collaboration Areas", formData.collaboration_areas.join(", ") || "—"],
            ["Host Intl. Delegates", formData.host_intl_delegates ? "Yes" : "No"],
            ["Authorized Signatory", formData.authorized_signatory_name],
            ["Declaration Date", formData.declaration_date],
            ["Consents Given", consentDone ? "Yes" : "No"],
          ].map(([k, v]) => (
            <div key={k} style={{
              display: "flex", padding: "9px 0",
              borderBottom: `1px solid ${T.light}`,
            }}>
              <span style={{ width: 180, fontSize: 12, color: T.muted, fontWeight: 700, flexShrink: 0 }}>{k}</span>
              <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{v || "—"}</span>
            </div>
          ))}
          <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setPreviewMode(false)}>Go Back & Edit</Btn>
            <Btn variant="primary" icon={Check} onClick={() => { setPreviewMode(false); handleSave("submitted"); }} disabled={!consentDone || saving}>
              {saving ? "Submitting…" : "Confirm & Submit"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Full render ────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FONT }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>

      {previewMode && <PreviewModal />}

      <h2 className="pt-1 pb-1 text-center" style={{ color: T.navy, fontSize: "18px", margin: 0, padding: "16px 0 0" }}>
        COLLEGE REGISTRATION
      </h2>

      <div style={{ width: "100%", padding: "24px 24px 48px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 24, alignItems: "start" }}>

          {/* ── Form area ── */}
          <div>
            <StepBar current={step} />

            <div style={{ marginTop: 18 }}>
              {step === 0 && renderStep0()}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </div>

            {/* Navigation */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 24, paddingBottom: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {step > 0 && (
                  <Btn variant="outline" onClick={() => setStep(s => s - 1)}>← Back</Btn>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {saveStatus === "saved" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.success, fontWeight: 700, fontSize: 13 }}>
                    <CheckCircle size={15} /> Saved
                  </span>
                )}
                {saveStatus === "error" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.danger, fontWeight: 700, fontSize: 13 }}>
                    <AlertCircle size={15} /> Save failed
                  </span>
                )}
                <Btn variant="secondary" icon={Save} onClick={() => handleSave("draft")} disabled={saving}>
                  Save Draft
                </Btn>
                {step < STEPS.length - 1 ? (
                  <Btn
                    variant="primary"
                    disabled={!validateStep(step)}
                    onClick={() => setStep(s => s + 1)}
                  >
                    Next →
                  </Btn>
                ) : (
                  <>
                    <Btn variant="navy" icon={Eye} onClick={() => setPreviewMode(true)}>
                      Preview
                    </Btn>
                    <Btn
                      variant="success"
                      icon={Check}
                      onClick={() => handleSave("submitted")}
                      disabled={saving || !consentDone}
                    >
                      {saving ? "Submitting…" : existingProfile ? "Update Registration" : "Submit Registration"}
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div style={{ position: "sticky", top: 24 }}>
            <Card style={{ padding: 20, marginBottom: 18 }}>
              <div style={{
                fontWeight: 800, fontSize: 13, color: T.navy, marginBottom: 14,
                textTransform: "uppercase", letterSpacing: .5,
              }}>
                Profile Completion
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
                <DonutChart pct={completion} />
              </div>
              <ProgressBar value={completion} color={completion > 80 ? T.success : T.orange} />

              {missingRequired.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                    Required Fields Missing
                  </div>
                  {missingRequired.map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.danger, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: T.text }}>
                        {f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {missingRequired.length === 0 && (
                <div style={{
                  marginTop: 14, display: "flex", alignItems: "center", gap: 6,
                  background: "rgba(22,163,74,.1)", borderRadius: 8, padding: "8px 12px",
                }}>
                  <CheckCircle size={14} color={T.success} />
                  <span style={{ fontSize: 12, color: T.success, fontWeight: 700 }}>
                    All required fields filled
                  </span>
                </div>
              )}

              <div style={{ marginTop: 18, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                  Status
                </div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: existingProfile ? "rgba(249,115,22,.1)" : T.light,
                  borderRadius: 8, padding: "6px 12px",
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: existingProfile ? T.orange : T.muted }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: existingProfile ? T.orange : T.muted }}>
                    {existingProfile ? "Profile Exists" : "Not Yet Saved"}
                  </span>
                </div>
              </div>

              {/* Declaration status */}
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                  Declaration
                </div>
                {[
                  ["Accuracy", formData.consent_accuracy],
                  ["Media Use", formData.consent_media],
                  ["Signatory", !!formData.authorized_signatory_name],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: val ? T.success : T.border, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: val ? T.success : T.muted, fontWeight: val ? 700 : 400 }}>{label}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Step guide */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>
                Steps
              </div>
              {STEPS.map((s, i) => (
                <div
                  key={i}
                  onClick={() => { if (i <= step || validateStep(step)) { setStep(i); } }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 8, marginBottom: 4,
                    cursor: "pointer",
                    background: step === i ? "rgba(249,115,22,.1)" : "transparent",
                    border: `1px solid ${step === i ? "rgba(249,115,22,.3)" : "transparent"}`,
                    transition: "all .15s",
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: i < step ? T.success : step === i ? T.orange : T.light,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {i < step
                      ? <Check size={11} color="#fff" strokeWidth={3} />
                      : <s.icon size={11} color={step === i ? "#fff" : T.muted} />}
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: step === i ? 700 : 500,
                    color: step === i ? T.orange : i < step ? T.success : T.muted,
                  }}>{s.label}</span>
                </div>
              ))}
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CollegeRegistration;